/* eslint-disable react/no-array-index-key */
import { useMemo, useState } from 'react'
import './CreateEditProfile.scss'
import { SelectPicker, Panel, ButtonToolbar, Col, Input } from 'rsuite'
import {
  Button,
  FormikErrorMessage,
  Modal,
  Row,
  Section,
  Uploader
} from 'shared'
import { notifyError, notifySuccess, previewFile } from 'utils'
import { Formik, Form, FormikHelpers, FormikProps, FieldArray } from 'formik'
import {
  PROFILE_FORM_FIELDS,
  convertFileToBase64,
  DOCUMENT_TYPE
} from '../utils'
import { useUpdateDocumentMutation } from '../profileApiSlice'

interface DocumentProfileProps {
  open: boolean;
  onClose: () => void;
  profile?: any;
  isEditMode?: boolean;
  selectedDocument?: any | null;
}

const DocumentEditProfile: React.FC<DocumentProfileProps> = ({
  open,
  onClose,
  profile,
  selectedDocument
}) => {
  const [editDocument] = useUpdateDocumentMutation()
  const [fileInfos, setFileInfos] = useState<Record<number, string>>({})

  const { DOCUMENTDTO } = PROFILE_FORM_FIELDS
  const MAX_FILE_SIZE = 2 * 1024 * 1024
  const companyId = profile?.company?.id
  const singleMode = !!selectedDocument

  const initialValues = useMemo(
    () => ({
      [DOCUMENTDTO]: singleMode
        ? [
            {
              id: selectedDocument?.id ?? null,
              documentType: selectedDocument?.documentType || '',
              documentNo: selectedDocument?.documentNo || '',
              imageUrls: selectedDocument?.imageUrls || null,
              fileInfo:
                typeof selectedDocument?.imageUrls === 'string'
                  ? selectedDocument.imageUrls
                  : ''
            }
          ]
        : (profile?.documentDTOS ?? []).map((d: any) => ({
            id: d.id ?? null,
            documentType: d.documentType || '',
            documentNo: d.documentNo || '',
            imageUrls: d.imageUrls || null,
            fileInfo: typeof d.imageUrls === 'string' ? d.imageUrls : ''
          })) || [
            { documentType: '', documentNo: '', imageUrls: null, fileInfo: '' }
          ]
    }),
    [singleMode, selectedDocument, profile, DOCUMENTDTO]
  )

  const acceptedDocTypes = useMemo(() => {
    const list = (profile?.documentDTOS ?? []) as Array<{
      documentType?: string,
      status?: string
    }>
    return new Set(
      list
        .filter((d) => (d?.status ?? '').toLowerCase() === 'accepted')
        .map((d) => d.documentType || '')
        .filter(Boolean)
    )
  }, [profile])

  const onSubmit = async (
    formValues: { [key: string]: any },
    { setSubmitting }: FormikHelpers<any>
  ) => {
    try {
      const docs = (formValues[DOCUMENTDTO] ?? []) as Array<any>

      if (!singleMode && docs.length !== 3) {
        notifyError('Please upload 3 documents', { containerId: 'modalToast' })
        setSubmitting(false)
        return
      }

      const processedDocs = await Promise.all(
        docs.map(async (doc) => {
          let output = doc.imageUrls
          if (
            output &&
            typeof output === 'string' &&
            output.startsWith('http')
          ) {
            const response = await fetch(output)
            const blob = await response.blob()
            const file = new File([blob], 'document', { type: blob.type })
            output = await convertFileToBase64(file)
          }
          return {
            id: doc.id ?? null,
            documentType: doc.documentType,
            documentNo: doc.documentNo || '',
            imageUrls: output
          }
        })
      )

      await editDocument({
        id: companyId,
        documentDTOS: processedDocs
      } as any).unwrap()

      notifySuccess(
        singleMode
          ? 'Document updated successfully!'
          : 'Documents updated successfully!'
      )
      onClose()
    } catch {
      notifyError('Failed to update document(s)', { containerId: 'modalToast' })
    } finally {
      setSubmitting(false)
    }
  }

  const renderFormButtons = (formikProps: FormikProps<any>) => (
    <ButtonToolbar>
      <Button
        className="formButton"
        id="reset"
        onClick={() => {
          formikProps.resetForm()
        }}
      >
        Reset
      </Button>
      <Button
        className="formButton"
        appearance="primary"
        type="submit"
        disabled={formikProps.isValidating ?? formikProps.isSubmitting}
      >
        Save
      </Button>
    </ButtonToolbar>
  )

  return (
    <Modal
      secondary
      open={open}
      onClose={onClose}
      title={singleMode ? 'Edit Document' : 'Edit Documents'}
      size="calc(50% - 120px)"
      body={
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={onSubmit}
        >
          {(formikProps: FormikProps<any>) => (
            <Form className="edit-profile">
              <Panel>
                <Section title={singleMode ? 'Document' : 'Company Documents'}>
                  <FieldArray name={DOCUMENTDTO}>
                    {(arrayHelpers) => {
                      const documents = formikProps.values[DOCUMENTDTO] ?? []

                      if (!documents.length) {
                        arrayHelpers.push({
                          documentType: '',
                          documentNo: '',
                          imageUrls: null,
                          fileInfo: ''
                        })
                      }

                      const selectedTypes = documents.map(
                        (d: any) => d.documentType
                      )
                      const documentOptions = Object.values(DOCUMENT_TYPE).map(
                        (value) => ({
                          label: value,
                          value
                        })
                      )

                      return (
                        <>
                          {documents.map((doc: any, index: number) => {
                            const availableOptions = documentOptions.filter(
                              (opt) =>
                                !selectedTypes.includes(opt.value) ||
                                opt.value === doc.documentType
                            )
                            const isLocked =
                              !!doc?.documentType &&
                              acceptedDocTypes.has(doc.documentType)

                            return (
                              <Row key={index}>
                                <Col xs={7}>
                                  <SelectPicker
                                    data={availableOptions}
                                    value={doc.documentType}
                                    onChange={(value) =>
                                      formikProps.setFieldValue(
                                        `${DOCUMENTDTO}.${index}.documentType`,
                                        value
                                      )
                                    }
                                    placeholder="Select Document Type"
                                    block
                                    disabled={isLocked}
                                  />
                                  <FormikErrorMessage
                                    name={`${DOCUMENTDTO}[${index}].documentType`}
                                  />
                                </Col>

                                <Col xs={7}>
                                  <Input
                                    name={`${DOCUMENTDTO}.${index}.documentNo`}
                                    value={doc.documentNo ?? ''}
                                    onChange={(value) =>
                                      formikProps.setFieldValue(
                                        `${DOCUMENTDTO}.${index}.documentNo`,
                                        value
                                      )
                                    }
                                    placeholder="Enter Document Number"
                                    disabled={isLocked}
                                  />
                                  <FormikErrorMessage
                                    name={`${DOCUMENTDTO}[${index}].documentNo`}
                                  />
                                </Col>

                                <Col xs={6}>
                                  <Uploader
                                    isVideo={false}
                                    draggable
                                    listType="picture"
                                    action="http://localhost:3000/post"
                                    fileInfo={
                                      typeof doc.fileInfo === 'string'
                                        ? doc.fileInfo
                                        : fileInfos[index]
                                    }
                                    isPdf={(typeof doc.fileInfo === 'string'
                                      ? doc.fileInfo
                                      : fileInfos[index]
                                    )?.endsWith?.('.pdf')}
                                    disabled={isLocked}
                                    onChange={async (fileList) => {
                                      if (fileList.length > 0) {
                                        const file =
                                          fileList[fileList.length - 1]
                                        if (!file?.blobFile) {
                                          notifyError('Invalid file selected', {
                                            containerId: 'modalToast'
                                          })
                                          return
                                        }
                                        const valid = [
                                          'image/jpeg',
                                          'image/jpg',
                                          'image/png',
                                          'image/gif',
                                          'image/svg+xml',
                                          'image/webp',
                                          'application/pdf'
                                        ]
                                        if (
                                          !valid.includes(file.blobFile.type)
                                        ) {
                                          notifyError(
                                            'Only Image and PDF files are allowed',
                                            { containerId: 'modalToast' }
                                          )
                                          fileList.pop()
                                          formikProps.setFieldValue(
                                            `${DOCUMENTDTO}.${index}.imageUrls`,
                                            null
                                          )
                                          return
                                        }
                                        if (
                                          file.blobFile.size > MAX_FILE_SIZE
                                        ) {
                                          notifyError(
                                            'Size must be less than 2MB',
                                            { containerId: 'modalToast' }
                                          )
                                          return
                                        }
                                        try {
                                          const base64 =
                                            await convertFileToBase64(
                                              file.blobFile
                                            )
                                          previewFile(
                                            file.blobFile,
                                            (value: any) => {
                                              setFileInfos((prev) => ({
                                                ...prev,
                                                [index]: value
                                              }))
                                              formikProps.setFieldValue(
                                                `${DOCUMENTDTO}.${index}.fileInfo`,
                                                value
                                              )
                                            }
                                          )
                                          fileList.splice(
                                            0,
                                            fileList.length - 1
                                          )
                                          formikProps.setFieldValue(
                                            `${DOCUMENTDTO}.${index}.imageUrls`,
                                            base64
                                          )
                                        } catch (err) {
                                          // eslint-disable-next-line no-console
                                          console.error('toBase64 error', err)
                                        }
                                      } else {
                                        formikProps.setFieldValue(
                                          `${DOCUMENTDTO}.${index}.imageUrls`,
                                          null
                                        )
                                      }
                                    }}
                                  />
                                  <FormikErrorMessage
                                    name={`${DOCUMENTDTO}[${index}].imageUrls`}
                                  />
                                </Col>
                              </Row>
                            )
                          })}
                        </>
                      )
                    }}
                  </FieldArray>
                </Section>
              </Panel>

              <Panel className="panel-buttons">
                {renderFormButtons(formikProps)}
              </Panel>
            </Form>
          )}
        </Formik>
      }
    />
  )
}

export default DocumentEditProfile
